import { useEffect, useRef, useState } from "react";
import { collection, query, where, getDocs, Timestamp } from "firebase/firestore";
import { Download, Link } from "lucide-react";
import { db } from "../firebase-config";
import { computeDailyStats } from "../dailyStatsLogic";
import { shareImageToPlatform, SITE_URL } from "../shareImage";
import { getEquippedItem } from "../cosmetics";
import AvatarRing from "./AvatarRing";
import SubtitleBadge from "./SubtitleBadge";
import DailyOutcomesCard from "./DailyOutcomesCard";
import { TRUST_COLOR, BETRAY_COLOR, TEXT_MUTED, BORDER, GRADIENT_BG, TEXT_DARK } from "../theme";

function XIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.632L18.244 2.25zM17.083 19.77h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  );
}
function WhatsAppIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
function FacebookIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function SharePlatformBtn({ label, icon, bg, onClick, active }) {
  return (
    <button
      onClick={onClick}
      disabled={!!active}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: "5px", padding: "10px 4px",
        background: bg, color: "#fff", border: "none", borderRadius: "8px",
        fontSize: "0.625rem", fontWeight: "700", opacity: active ? 0.6 : 1,
      }}
    >
      {icon}
      {active ? "…" : label}
    </button>
  );
}

function DailyStats({ onClose, userProfile, shopItems = [] }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharing, setSharing] = useState(null);
  const cardRef = useRef(null);

  const shareText = "today's trust game outcomes";

  const doShare = async (platform) => {
    setSharing(platform);
    const result = await shareImageToPlatform(cardRef.current, {
      platform,
      text: shareText,
      url: SITE_URL,
    });
    setSharing(null);
    if (!result.ok) alert(result.error);
  };

  const copyLink = () => navigator.clipboard.writeText(SITE_URL).then(() => alert("link copied!"));

  useEffect(() => {
    loadTodayStats();
  }, []);

  const loadTodayStats = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const gamesQuery = query(
        collection(db, "games"),
        where("state", "==", "results"),
        where("finishedAt", ">=", Timestamp.fromDate(today))
      );
      const snapshot = await getDocs(gamesQuery);
      setStats(computeDailyStats(snapshot.docs.map((d) => d.data())));
    } catch (err) {
      console.error("Error loading stats:", err);
      setError(err.message || "failed to load today's statistics.");
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div style={{ padding: 20, textAlign: "center", maxWidth: "500px", margin: "50px auto" }}>
        <p style={{ color: BETRAY_COLOR, fontSize: "0.8125rem" }}>couldn't load today's statistics</p>
        <p style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>{error}</p>
        <button onClick={onClose} style={{ padding: "8px 18px", background: "#fff", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "8px", marginTop: "10px" }}>close</button>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, maxWidth: "560px", margin: "40px auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: "700" }}>today's outcomes</h2>
        <button onClick={onClose} style={{ padding: "6px 14px", background: "#fff", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "0.8125rem" }}>close</button>
      </div>

      {/* Capture area includes user context header + outcomes card */}
      <div ref={cardRef}>
        {userProfile && (
          <div style={{ background: GRADIENT_BG, borderRadius: "16px 16px 0 0", padding: "16px 20px", display: "flex", alignItems: "center", gap: "12px" }}>
            <AvatarRing
              photoURL={userProfile.photoURL}
              size={40}
              item={getEquippedItem(userProfile, shopItems, "border")}
            />
            <div>
              <div style={{ fontWeight: "700", fontSize: "0.875rem", color: TEXT_DARK, display: "flex", alignItems: "center", gap: "6px" }}>
                {userProfile.displayName}
                <SubtitleBadge item={getEquippedItem(userProfile, shopItems, "subtitle")} />
              </div>
              <div style={{ fontSize: "0.6875rem", color: TEXT_MUTED }}>played today's trust game</div>
            </div>
          </div>
        )}
        <DailyOutcomesCard
          stats={stats}
          loading={loading}
          emptyLabel="no games played today yet"
          topRounded={!userProfile}
        />
      </div>

      {stats && stats.totalGames > 0 && (
        <div style={{ marginTop: "14px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "8px", marginBottom: "10px" }}>
            <SharePlatformBtn label="tweet"    icon={<XIcon size={17} />}        bg="#000"     onClick={() => doShare("twitter")}  active={sharing === "twitter"} />
            <SharePlatformBtn label="facebook" icon={<FacebookIcon size={17} />} bg="#1877F2"  onClick={() => doShare("facebook")} active={sharing === "facebook"} />
            <SharePlatformBtn label="whatsapp" icon={<WhatsAppIcon size={17} />} bg="#25D366"  onClick={() => doShare("whatsapp")} active={sharing === "whatsapp"} />
            <SharePlatformBtn label="image"    icon={<Download size={17} />}     bg={TRUST_COLOR} onClick={() => doShare("image")} active={sharing === "image"} />
          </div>
          <button onClick={copyLink} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "9px", fontSize: "0.8125rem", fontWeight: "600", background: "#fff", color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: "8px" }}>
            <Link size={14} /> copy link
          </button>
        </div>
      )}
    </div>
  );
}

export default DailyStats;
